import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { teacherModuleAPI } from '../../services/api';

function shiftMonth(month, delta) {
  const [y, m] = month.split('-').map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function formatMonthLabel(month) {
  const [y, m] = month.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleString('en-IN', { month: 'long', year: 'numeric' });
}

const STATUS_LABELS = {
  not_started: 'Not started',
  draft: 'Draft',
  submitted: 'Submitted',
  approved: 'Approved',
  rejected: 'Needs revision',
};

const STATUS_COLOR = {
  not_started: 'default',
  draft: 'warning',
  submitted: 'info',
  approved: 'success',
  rejected: 'error',
};

export default function StudentMonthlyReports() {
  const navigate = useNavigate();
  const now = new Date();
  // Current month first — teachers fill assessments at month-end for parent send in week 1 of next month.
  const defaultMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const [month, setMonth] = useState(defaultMonth);

  const listQuery = useQuery(
    ['studentMonthlyReports', month],
    async () => {
      const res = await teacherModuleAPI.listStudentMonthlyReports({ month });
      return res.data?.data || [];
    },
    { enabled: !!month }
  );

  const readinessQuery = useQuery(
    ['studentReportReadiness', month],
    async () => {
      const res = await teacherModuleAPI.getStudentReportReadiness({ month });
      return res.data?.data;
    },
    { enabled: !!month }
  );

  const rows = listQuery.data || [];
  const readiness = readinessQuery.data;
  const monthLabel = formatMonthLabel(month);

  const summary = useMemo(() => {
    if (!readiness) return null;
    return `${readiness.reports?.submitted || 0} of ${readiness.eligibleStudents || 0} submitted · ${readiness.reports?.approved || 0} approved`;
  }, [readiness]);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2} mb={2}>
        <Box>
          <Typography variant="h5" fontWeight={700}>
            My student assessments
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {monthLabel} — click a student name to open their report form
          </Typography>
        </Box>
        <TextField
          select
          size="small"
          label="Month"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          sx={{ minWidth: 200 }}
        >
          {[0, 1, 2, 3, 4, 5].map((i) => {
            const m = shiftMonth(defaultMonth, -i);
            return (
              <MenuItem key={m} value={m}>
                {formatMonthLabel(m)}
              </MenuItem>
            );
          })}
        </TextField>
      </Stack>

      {summary && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {summary} · Achievement report: {readiness.achievementStatus || 'not started'}
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 2 }}>
        Students appear here only after they have at least one present or makeup attendance in your
        class for {monthLabel}. Until then, their assessment cannot be opened.
      </Alert>

      {listQuery.isLoading ? (
        <CircularProgress />
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="medium">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell sx={{ fontWeight: 700 }}>Student</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Batch</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">
                  Open
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => {
                const name = `${row.firstName || ''} ${row.lastName || ''}`.trim();
                const status = row.status || 'not_started';
                return (
                  <TableRow key={row.studentId} hover>
                    <TableCell>
                      <Typography fontWeight={600}>{name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {row.displayId}
                      </Typography>
                    </TableCell>
                    <TableCell>{row.batchName || '—'}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Chip
                          size="small"
                          label={STATUS_LABELS[status] || status}
                          color={STATUS_COLOR[status] || 'default'}
                          variant="outlined"
                        />
                        {row.isInherited ? (
                          <Chip size="small" label="Previous teacher" color="default" variant="filled" />
                        ) : null}
                      </Stack>
                      {row.isInherited && row.reportingTeacherName ? (
                        <Typography variant="caption" color="text.secondary" display="block">
                          By {row.reportingTeacherName}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => navigate(`/teacher/student-reports/${row.studentId}/${month}`)}
                      >
                        {row.isInherited || (status !== 'not_started' && status !== 'draft' && status !== 'rejected')
                          ? 'View'
                          : status === 'not_started'
                            ? 'Fill report'
                            : 'Open'}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
              {!rows.length && (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    No eligible students yet. Mark present/makeup attendance first.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
